import React, { useEffect, useState } from 'react';
import UploadDropzone from './UploadDropzone';
import { CLOUDINARY_CLOUD_NAME, CLOUDINARY_UPLOAD_PRESET, CLOUDINARY_PRODUCTS_FOLDER } from '../config';
import { storage } from '../firebase';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

const ProductEditForm = ({ value, categories, onSave, submitting, onCancel }) => {
  const [form, setForm] = useState(value || {});
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => { setForm(value || {}); }, [value]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const uploadImageIfNeeded = async () => {
    if (!file) return null;
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
    const allowed = ['png','jpg','jpeg','webp','avif'];
    if (!allowed.includes(ext)) throw new Error('Formato não suportado.');

    if (CLOUDINARY_CLOUD_NAME && CLOUDINARY_UPLOAD_PRESET) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      if (CLOUDINARY_PRODUCTS_FOLDER) formData.append('folder', CLOUDINARY_PRODUCTS_FOLDER);
      formData.append('public_id', `${(form.name || 'produto').toLowerCase().replace(/[^a-z0-9-]+/g,'-')}-${Date.now()}`);
      const endpoints = [
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/auto/upload`,
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
      ];
      let lastErr;
      for (const url of endpoints) {
        try {
          const res = await fetch(url, { method: 'POST', body: formData });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) { lastErr = new Error(data?.error?.message || 'Falha ao enviar imagem'); continue; }
          if (data?.secure_url || data?.url) return data.secure_url || data.url;
          lastErr = new Error('Resposta inesperada do Cloudinary');
        } catch (e) { lastErr = e; }
      }
      throw lastErr || new Error('Falha ao enviar imagem');
    }

    const path = `products/${Date.now()}-${file.name}`;
    const ref = storageRef(storage, path);
    const task = uploadBytesResumable(ref, file, { contentType: file.type });
    return new Promise((resolve, reject) => {
      task.on('state_changed', (snap) => {
        setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100));
      }, reject, async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        resolve(url);
      });
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let imgUrl = form.img;
    if (file) imgUrl = await uploadImageIfNeeded();
    onSave({ ...form, img: imgUrl });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
        <input name="name" value={form.name || ''} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67441]" required />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
        <select name="type" value={form.type || ''} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67441]" required>
          <option value="">Selecione...</option>
          {categories.map((c) => (
            <option key={c.id} value={c.slug || c.id}>{c.title || c.name}</option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
          <input name="brand" value={form.brand || ''} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67441]" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Espessura / Especificação</label>
          <input name="thickness" value={form.thickness || ''} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67441]" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Destaque</label>
          <input name="feature" value={form.feature || ''} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67441]" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Ambientes</label>
          <input name="environments" value={form.environments || ''} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67441]" />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
        <textarea name="description" value={form.description || ''} onChange={handleChange} rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67441]" />
      </div>
      <UploadDropzone label="Imagem do Produto" onFileSelected={setFile} previewUrl={file ? URL.createObjectURL(file) : form.img} progress={progress} />
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">Cancelar</button>
        <button type="submit" disabled={submitting} className="px-4 py-2 rounded-lg bg-[#D67441] text-white font-bold hover:bg-[#B85A35] disabled:opacity-70">{submitting ? 'Salvando...' : 'Salvar'}</button>
      </div>
    </form>
  );
};

export default ProductEditForm; 